import { useContext, useEffect, useState, useMemo, useCallback } from "react";
import { UserContext } from "../../../hooks/UserContext";
import { API, Company } from "../../../api";
import { CompanySettings, ImportTableColumn } from "../../../utilities/types";
import { ColumnSetting, ImportSettingsViewModel, columnDisplayNames, hardcodedColumns } from "./ImportSettings.types";
import { getDefaultColumnSettings, applyHardcodedRules, validateColumnChange } from "./ImportSettings.helpers";
import { Oops } from "../../../utilities/oops";
import { LogData } from "@rogoag/shared-ts";
import { logger } from "../../../utilities/logger";

/**
 * Custom hook that implements the ViewModel for ImportSettings
 */
export function useImportSettingsViewModel(): ImportSettingsViewModel {
	const userContext = useContext(UserContext);

	// State
	const [companies, setCompanies] = useState<Company[]>([]);
	const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
	const [sourceCompany, setSourceCompany] = useState<Company | null>(null);
	const [columnSettings, setColumnSettings] = useState<Record<string, ColumnSetting>>({});
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [copying, setCopying] = useState(false);
	const [originalSettings, setOriginalSettings] = useState<Record<string, ColumnSetting>>({});

	// Computed: Permission check
	const isSuperUser = useMemo(() => userContext?.isSuperUser ?? false, [userContext]);

	logger.entrypoint = "ImportSettings";

	// Effect: Fetch companies on mount
	useEffect(() => {
		const fetchCompanies = async () => {
			setLoading(true);
			try {
				const allCompanies = await API.Data.Companies.get.byFilter({
					type: "OR",
					fields: [{ isActive: 1 }],
				});
				setCompanies(allCompanies);
			} catch (error) {
				Oops.handleError(error, "ImportSettings.ViewModel.fetchCompanies");
			} finally {
				setLoading(false);
			}
		};
		fetchCompanies();
	}, []);

	// Effect: Load settings when company is selected
	useEffect(() => {
		if (!selectedCompany) return;

		let settings: Record<string, ColumnSetting>;

		if (selectedCompany.portalSettings?.importTableColumns) {
			settings = { ...selectedCompany.portalSettings.importTableColumns };
		} else {
			// Initialize with default settings from DefaultImportTableColumns if none exist
			settings = getDefaultColumnSettings();
		}

		// Apply hardcoded field values
		settings = applyHardcodedRules(settings);

		setColumnSettings(settings);
		setOriginalSettings(settings);
	}, [selectedCompany]);

	// Event Handler: Select company
	const onSelectCompany = useCallback((company: Company | null) => {
		setSelectedCompany(company);
		setSourceCompany(null); // Reset source company when changing selected company
	}, []);

	// Event Handler: Select source company for copying
	const onSelectSourceCompany = useCallback((company: Company | null) => {
		setSourceCompany(company);
	}, []);

	// Event Handler: Change column visibility or required status
	const onColumnChange = useCallback(
		(column: ImportTableColumn, field: "visible" | "required", value: boolean) => {
			// Validate the change using Model function
			if (!validateColumnChange(column, field, value, columnSettings)) {
				return;
			}

			setColumnSettings((prev) => {
				const currentSetting = prev[column] || { visible: false, required: false };

				// If setting required to true, also set visible to true
				if (field === "required" && value === true) {
					return {
						...prev,
						[column]: {
							visible: true,
							required: true,
						},
					};
				}

				// If trying to uncheck visible while required is checked, prevent it (already validated above)
				if (field === "visible" && value === false && currentSetting.required) {
					return prev;
				}

				return {
					...prev,
					[column]: {
						...currentSetting,
						[field]: value,
					},
				};
			});
		},
		[columnSettings],
	);

	// Event Handler: Save settings
	const onSave = useCallback(async () => {
		if (!selectedCompany) return;

		setSaving(true);
		try {
			const logData: LogData = {
				function: "ImportSettings.viewmodel.onSave",
				airtable_record_id: selectedCompany.id,
				airtable_table: "Companies",
			};
			logger.log("Saving import settings", logData);
			const updatedSettings = {
				...(selectedCompany.portalSettings || { views: [] }),
				importTableColumns: columnSettings as CompanySettings["importTableColumns"],
			};

			await API.Data.Companies.update.portalSettings(selectedCompany.id, updatedSettings);
			setOriginalSettings(columnSettings);

			// Update local company data
			setCompanies((prev) =>
				prev.map((c) => (c.id === selectedCompany.id ? { ...c, portalSettings: updatedSettings } : c)),
			);
			// Update the selected company with new settings
			setSelectedCompany({ ...selectedCompany, portalSettings: updatedSettings });
			logger.log("Import settings saved successfully", logData);
		} catch (error) {
			Oops.handleError(error, "ImportSettings.ViewModel.onSave");
		} finally {
			setSaving(false);
		}
	}, [selectedCompany, columnSettings]);

	// Event Handler: Copy settings from source company
	const onCopySettings = useCallback(() => {
		if (!sourceCompany || !selectedCompany) return;

		setCopying(true);
		try {
			let copiedSettings: Record<string, ColumnSetting>;

			if (sourceCompany.portalSettings?.importTableColumns) {
				copiedSettings = JSON.parse(JSON.stringify(sourceCompany.portalSettings.importTableColumns));
			} else {
				// Copy default settings if source company has no custom settings
				copiedSettings = getDefaultColumnSettings();
			}

			// Apply hardcoded field values to copied settings
			copiedSettings = applyHardcodedRules(copiedSettings);

			setColumnSettings(copiedSettings);
		} catch (error) {
			Oops.handleError(error, "ImportSettings.ViewModel.onCopySettings");
		} finally {
			setCopying(false);
		}
	}, [sourceCompany, selectedCompany]);

	// Event Handler: Reset to defaults
	const onResetToDefaults = useCallback(() => {
		if (!selectedCompany) return;

		let defaultSettings = getDefaultColumnSettings();
		defaultSettings = applyHardcodedRules(defaultSettings);

		setColumnSettings(defaultSettings);
	}, [selectedCompany]);

	// Event Handler: Set all visible
	const onSetAllVisible = useCallback((visible: boolean) => {
		setColumnSettings((prev) => {
			const newSettings = { ...prev };

			Object.keys(columnDisplayNames).forEach((column) => {
				const hardcoded = hardcodedColumns[column as ImportTableColumn];
				const currentSetting = newSettings[column] || { visible: false, required: false };

				// Skip hardcoded visible fields
				if (hardcoded?.alwaysVisible) {
					return;
				}

				// If setting to invisible, also set required to false (unless hardcoded required)
				if (!visible && !hardcoded?.alwaysRequired) {
					newSettings[column] = { visible: false, required: false };
				} else {
					// If setting to visible, keep the existing required state
					newSettings[column] = { ...currentSetting, visible };
				}
			});

			return newSettings;
		});
	}, []);

	// Event Handler: Set all required
	const onSetAllRequired = useCallback((required: boolean) => {
		setColumnSettings((prev) => {
			const newSettings = { ...prev };

			Object.keys(columnDisplayNames).forEach((column) => {
				const hardcoded = hardcodedColumns[column as ImportTableColumn];
				const currentSetting = newSettings[column] || { visible: false, required: false };

				// Skip hardcoded required fields
				if (hardcoded?.alwaysRequired) {
					return;
				}

				// If setting to required, also set visible to true
				if (required) {
					newSettings[column] = { visible: true, required: true };
				} else {
					// If setting to not required, keep the existing visible state
					newSettings[column] = { ...currentSetting, required: false };
				}
			});

			return newSettings;
		});
	}, []);

	// Event Handler: Cancel changes
	const onCancel = useCallback(() => {
		setColumnSettings(originalSettings);
	}, [originalSettings]);

	// Computed: All non-hardcoded visible
	const allNonHardcodedVisible = useMemo(() => {
		return Object.keys(columnDisplayNames).every((column) => {
			const hardcoded = hardcodedColumns[column as ImportTableColumn];
			// If it's hardcoded to be visible, skip it
			if (hardcoded?.alwaysVisible) {
				return true;
			}
			// Check if the column is visible
			return columnSettings[column]?.visible || false;
		});
	}, [columnSettings]);

	// Computed: All visible non-hardcoded required
	const allVisibleNonHardcodedRequired = useMemo(() => {
		return Object.keys(columnDisplayNames).every((column) => {
			const hardcoded = hardcodedColumns[column as ImportTableColumn];
			const setting = columnSettings[column];

			// If it's hardcoded to be required, skip it
			if (hardcoded?.alwaysRequired) {
				return true;
			}

			// If not visible, skip it
			if (!setting?.visible) {
				return true;
			}

			// Check if the visible column is required
			return setting?.required || false;
		});
	}, [columnSettings]);

	// Computed: Has changes
	const hasChanges = useMemo(() => {
		return JSON.stringify(columnSettings) !== JSON.stringify(originalSettings);
	}, [columnSettings, originalSettings]);

	// Helper: Is visible disabled
	const isVisibleDisabled = useCallback(
		(column: ImportTableColumn) => {
			const hardcoded = hardcodedColumns[column];
			// Disabled if hardcoded visible or if required is checked
			return !!hardcoded?.alwaysVisible || !!columnSettings[column]?.required;
		},
		[columnSettings],
	);

	// Helper: Is required disabled
	const isRequiredDisabled = useCallback(
		(column: ImportTableColumn) => {
			const hardcoded = hardcodedColumns[column];
			// Disabled if not visible or if hardcoded required
			return !columnSettings[column]?.visible || !!hardcoded?.alwaysRequired;
		},
		[columnSettings],
	);

	/**
	 * Get the hardcoded label for a column (e.g., "(Always Visible & Required)")
	 * @returns The label string or null if not hardcoded
	 */
	const getHardcodedLabel = useCallback((column: ImportTableColumn): string | null => {
		const hardcoded = hardcodedColumns[column];
		if (!hardcoded || (!hardcoded.alwaysVisible && !hardcoded.alwaysRequired)) {
			return null;
		}

		if (hardcoded.alwaysVisible && hardcoded.alwaysRequired) {
			return "(Always Visible & Required)";
		}
		if (hardcoded.alwaysVisible) {
			return "(Always Visible)";
		}
		if (hardcoded.alwaysRequired) {
			return "(Always Required)";
		}

		return null;
	}, []);

	// Return ViewModel interface
	return {
		// State
		companies,
		selectedCompany,
		sourceCompany,
		columnSettings,
		loading,
		saving,
		copying,

		// Event handlers
		onSelectCompany,
		onSelectSourceCompany,
		onColumnChange,
		onSave,
		onCopySettings,
		onResetToDefaults,
		onSetAllVisible,
		onSetAllRequired,
		onCancel,

		// Computed values
		allNonHardcodedVisible,
		allVisibleNonHardcodedRequired,
		hasChanges,

		// Helper functions
		isVisibleDisabled,
		isRequiredDisabled,
		getHardcodedLabel,

		// Permission
		isSuperUser,
	};
}
